import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { ToastrService } from 'ngx-toastr';
import { filter, finalize, take } from 'rxjs';
import * as fromGenerated from '../_generated';
import type { MemoryDeletedEvent } from '../analytics/analytics.events';
import { AnalyticsService } from '../analytics/analytics.service';
import * as fromAuth from '../auth';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';

// The memory file's kinds (SB-4029), in the order the Figma sections list them.
const SECTIONS: { type: fromGenerated.UserMemoryEntryType; label: string }[] = [
  { type: 'about_me', label: 'SETTINGS.MEMORY.SECTIONS.ABOUT_ME' },
  { type: 'work', label: 'SETTINGS.MEMORY.SECTIONS.WORK' },
  { type: 'tools', label: 'SETTINGS.MEMORY.SECTIONS.TOOLS' },
  { type: 'preference', label: 'SETTINGS.MEMORY.SECTIONS.PREFERENCES' },
  { type: 'other', label: 'SETTINGS.MEMORY.SECTIONS.OTHER' },
];

// memory_deleted's catalogue values (SB-6520 KD-7) differ from the file's `preference` by one letter.
const CATEGORY: Record<
  fromGenerated.UserMemoryEntryType,
  MemoryDeletedEvent['category']
> = {
  // eslint-disable-next-line camelcase
  about_me: 'about_me',
  work: 'work',
  tools: 'tools',
  preference: 'preferences',
  other: 'other',
};

const NOTE_CLASS =
  'm-0 flex items-start gap-2 rounded-lg border px-3 py-2.5 typo-p3 leading-5';
const TIP_CLASS = fromSeriousSystem.cn(
  NOTE_CLASS,
  'border-neutral-100 bg-neutral-50',
);
const PAUSED_CLASS = fromSeriousSystem.cn(
  NOTE_CLASS,
  'border-warning-100 bg-warning-50 text-warning-800',
);

/**
 * The Memory pane of the settings window (SB-6520 Rules 4–7, Figma 13888-1990): the on/pause
 * switch, what Squadbox never keeps, and every saved memory under its section, newest first, each
 * deletable after a confirm, and all of it clearable at once.
 *
 * Memory text is untrusted (AI-written from what users say): it is only ever visible text,
 * isolated for direction, never in an attribute that error reporting records (KD-13).
 */
@Component({
  selector: 'squadbox-settings-memory',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-1 flex-col gap-4' },
  imports: [
    FormsModule,
    TranslateModule,
    fromSeriousSystem.ButtonDirective,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.SpinnerDirective,
    fromSeriousSystem.ToggleComponent,
    fromSeriousSystem.UseUIBasicIconDirective,
  ],
  template: `
    <div class="flex flex-col gap-4 px-3">
      <div class="flex items-center gap-3">
        <div class="flex flex-1 min-w-0 flex-col gap-1">
          <span class="typo-p3 leading-5 font-semibold text-neutral-900">{{
            'SETTINGS.MEMORY.TOGGLE' | translate
          }}</span>
          <span class="typo-p3 leading-5 text-neutral-500">{{
            'SETTINGS.MEMORY.TOGGLE_HELP' | translate
          }}</span>
        </div>
        <!-- The visible text above is the design's; the switch keeps its own label for its
             accessible name, visually hidden. It ignores input while a change saves (5.5, 5.6). -->
        <sd-toggle
          size="sm"
          class="shrink-0 [&_label]:sr-only"
          [label]="'SETTINGS.MEMORY.TOGGLE' | translate"
          [checked]="enabled()"
          [unavailable]="togglePending()"
          [ngModel]="enabled()"
          (ngModelChange)="setEnabled($event)"
        ></sd-toggle>
      </div>

      <div class="flex flex-col gap-2">
        @if (enabled()) {
          <p [class]="tipClass">
            <svg
              sdUseUIBasicIcon="info"
              class="size-5 shrink-0 text-neutral-500"
            ></svg>
            <span
              [innerHTML]="'SETTINGS.MEMORY.NEVER_KEEPS' | translate"
            ></span>
          </p>
          <p [class]="tipClass">
            <svg
              sdUseUIBasicIcon="bulb"
              class="size-5 shrink-0 text-neutral-500"
            ></svg>
            <span [innerHTML]="'SETTINGS.MEMORY.HOW_TO' | translate"></span>
          </p>
        } @else {
          <p [class]="pausedClass">
            <svg sdUseUIBasicIcon="info" class="size-5 shrink-0"></svg>
            <span [innerHTML]="'SETTINGS.MEMORY.PAUSED' | translate"></span>
          </p>
        }
      </div>
    </div>

    <hr class="my-2 border-neutral-200" />

    @if (loadError()) {
      <div class="flex flex-col items-center gap-3 py-10 text-center">
        <p class="m-0 typo-p3 leading-5 text-neutral-700">
          {{ 'SETTINGS.MEMORY.LOAD_ERROR' | translate }}
        </p>
        <button
          sdButton
          variant="outline"
          color="neutral"
          size="xs"
          (click)="load()"
        >
          {{ 'SETTINGS.MEMORY.RETRY' | translate }}
        </button>
      </div>
    } @else if (entries() === null) {
      <div class="flex flex-1 items-center justify-center py-10">
        <div sdSpinner></div>
      </div>
    } @else if (entries()?.length === 0) {
      <p
        class="m-0 flex flex-1 items-center justify-center py-10 text-center typo-p3 leading-5 text-neutral-400"
      >
        {{ 'SETTINGS.MEMORY.EMPTY' | translate }}
      </p>
    } @else {
      @for (group of groups(); track group.type) {
        <section>
          <h3
            class="m-0 border-b border-neutral-100 py-2 pl-3 typo-p3 leading-5 font-semibold text-neutral-900"
          >
            {{ group.label | translate }}
          </h3>
          <ul class="m-0 list-none p-0">
            @for (entry of group.entries; track entry.id) {
              <!-- The design's 8-column row (6 text, 2 date + delete) from tablet-landscape up;
                   on a phone the date goes under the text and the delete stays on the right. The
                   text starts 6px down so its first line, the date and the 32px delete share one
                   centre line however many lines the memory wraps to (spec 4.3). -->
              <li
                class="group grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 border-b border-neutral-100 px-3 py-2 tablet-landscape:grid-cols-8 tablet-landscape:gap-x-6"
              >
                <div
                  class="flex min-w-0 flex-col gap-0.5 self-start pt-1.5 tablet-landscape:col-span-6"
                >
                  <p
                    dir="auto"
                    class="m-0 break-words typo-p3 leading-5 text-neutral-900"
                  >
                    {{ entry.content }}
                  </p>
                  <span
                    class="typo-caption leading-4 text-neutral-500 tablet-landscape:hidden"
                    >{{ formatDate(entry.date) }}</span
                  >
                </div>
                <div
                  class="flex h-8 items-center justify-end gap-1 self-start tablet-landscape:col-span-2 tablet-landscape:justify-between"
                >
                  <span
                    class="hidden typo-caption leading-4 whitespace-nowrap text-neutral-500 tablet-landscape:inline"
                    >{{ formatDate(entry.date) }}</span
                  >
                  <!-- Shown on hover like the design, on keyboard focus, and always where there is
                       no hover. Its name is generic: never the memory itself (KD-13). -->
                  <button
                    sdIconButton
                    variant="icon"
                    color="neutral"
                    size="xs"
                    class="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
                    [disabled]="clearing() || deletingIds().has(entry.id)"
                    [attr.aria-label]="'SETTINGS.MEMORY.DELETE' | translate"
                    (click)="confirmDelete(entry)"
                  >
                    <svg sdUseUIBasicIcon="delete"></svg>
                  </button>
                </div>
              </li>
            }
          </ul>
        </section>
      }
      <!-- Below the last section, so it is only reached after seeing what it removes (Rule 7). -->
      <div class="px-3">
        <button
          sdButton
          variant="text"
          color="error"
          size="xs"
          [disabled]="clearing()"
          (click)="confirmClear()"
        >
          {{ 'SETTINGS.MEMORY.CLEAR_ALL' | translate }}
        </button>
      </div>
    }
  `,
})
export class SettingsMemoryComponent {
  private readonly store = inject(Store);
  private readonly usersService = inject(fromGenerated.UsersService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly translateService = inject(TranslateService);
  private readonly toastrService = inject(ToastrService);
  private readonly analyticsService = inject(AnalyticsService);

  protected readonly tipClass = TIP_CLASS;
  protected readonly pausedClass = PAUSED_CLASS;
  private readonly user = this.store.selectSignal(
    fromAuth.authFeature.selectUser,
  );
  protected readonly enabled = computed(
    () => this.user()?.memoryEnabled ?? true,
  );
  protected readonly togglePending = this.store.selectSignal(
    fromAuth.authFeature.selectMemoryTogglePending,
  );
  /** Null while loading. */
  protected readonly entries = signal<
    fromGenerated.UserMemoryEntryView[] | null
  >(null);
  protected readonly loadError = signal(false);
  /** A second Delete on a row already being deleted does nothing. */
  protected readonly deletingIds = signal<ReadonlySet<string>>(new Set());
  protected readonly clearing = signal(false);

  /** Only the sections that hold something; each newest first, as the API orders them (to the second). */
  protected readonly groups = computed(() =>
    SECTIONS.map((section) => ({
      ...section,
      entries: (this.entries() ?? []).filter(
        (entry) => entry.type === section.type,
      ),
    })).filter((section) => section.entries.length > 0),
  );

  // "Oct 6, 2026" / "6 oct. 2026", as in the design.
  private readonly dateFormat = new Intl.DateTimeFormat(
    this.translateService.getCurrentLang(),
    { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' },
  );
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.load();
  }

  /** Read on every open of the pane, and again from "Try again" (4.6). */
  protected load(): void {
    this.loadError.set(false);
    this.entries.set(null);
    this.usersService
      .usersControllerGetMyMemoryV1()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ entries }) => this.entries.set(entries),
        error: () => this.loadError.set(true),
      });
  }

  protected formatDate(isoDay: string): string {
    return this.dateFormat.format(new Date(`${isoDay}T00:00:00Z`));
  }

  protected setEnabled(enabled: boolean): void {
    if (this.togglePending()) return;
    this.store.dispatch(fromAuth.AuthActions.updateMemoryEnabled({ enabled }));
  }

  protected confirmDelete(entry: fromGenerated.UserMemoryEntryView): void {
    this.confirmDialog
      .confirm({
        title: this.t('SETTINGS.MEMORY.DELETE_CONFIRM.TITLE'),
        message: this.t('SETTINGS.MEMORY.DELETE_CONFIRM.MESSAGE', {
          content: entry.content,
        }),
        // Bold by the dialog's own plain-text split, never built from HTML (KD-13).
        emphasis: entry.content,
        confirmLabel: this.t('SETTINGS.MEMORY.DELETE_CONFIRM.CONFIRM'),
        cancelLabel: this.t('SETTINGS.MEMORY.DELETE_CONFIRM.CANCEL'),
        confirmType: 'delete',
      })
      .pipe(filter(Boolean), take(1))
      .subscribe(() => this.delete(entry));
  }

  protected confirmClear(): void {
    this.confirmDialog
      .confirm({
        title: this.t('SETTINGS.MEMORY.CLEAR_CONFIRM.TITLE'),
        message: this.t('SETTINGS.MEMORY.CLEAR_CONFIRM.MESSAGE'),
        confirmLabel: this.t('SETTINGS.MEMORY.CLEAR_CONFIRM.CONFIRM'),
        cancelLabel: this.t('SETTINGS.MEMORY.CLEAR_CONFIRM.CANCEL'),
        confirmType: 'delete',
      })
      .pipe(filter(Boolean), take(1))
      .subscribe(() => this.clear());
  }

  /** The row stays until the API confirms; an entry already gone counts as deleted (6.7, 6.8). */
  private delete(entry: fromGenerated.UserMemoryEntryView): void {
    if (this.deletingIds().has(entry.id) || this.clearing()) return;
    this.deletingIds.update((ids) => new Set(ids).add(entry.id));
    this.usersService
      .usersControllerDeleteMyMemoryEntryV1(entry.id)
      .pipe(finalize(() => this.forget(entry.id)))
      .subscribe({
        next: () => {
          this.entries.update(
            (entries) =>
              entries?.filter((item) => item.id !== entry.id) ?? null,
          );
          this.toastrService.success(this.t('SETTINGS.MEMORY.TOASTS.DELETED'));
          this.analyticsService
            .sendMemoryDeletedEvent({ category: CATEGORY[entry.type] })
            .subscribe();
        },
        error: () =>
          this.toastrService.error(this.t('SETTINGS.MEMORY.ERRORS.DELETE')),
      });
  }

  /** Clears everything the user saw; the switch keeps its position, conversations stay (Rule 7). */
  private clear(): void {
    if (this.clearing()) return;
    const count = this.entries()?.length ?? 0;
    this.clearing.set(true);
    this.usersService
      .usersControllerClearMyMemoryV1()
      .pipe(finalize(() => this.clearing.set(false)))
      .subscribe({
        next: () => {
          this.entries.set([]);
          this.toastrService.success(this.t('SETTINGS.MEMORY.TOASTS.CLEARED'));
          this.analyticsService.sendMemoryClearedEvent({ count }).subscribe();
        },
        error: () =>
          this.toastrService.error(this.t('SETTINGS.MEMORY.ERRORS.CLEAR')),
      });
  }

  private forget(id: string): void {
    this.deletingIds.update((ids) => {
      const next = new Set(ids);
      next.delete(id);
      return next;
    });
  }

  private t(key: string, params?: object): string {
    return this.translateService.instant(key, params) as string;
  }
}
